import { useLiveQuery } from "dexie-react-hooks";
import { Columns2, Copy, Download, FolderOpen, History, Star, Trash2, Upload } from "lucide-react";
import { useState } from "react";
import { PALETTE_KEYS } from "@/model/palette";
import { cloneTheme, type Theme } from "@/model/theme";
import { db, savePreset } from "@/presets/repo";
import { STARTERS, starterTheme } from "@/presets/starters";
import { useEditor } from "@/store/editor";
import { useUi } from "@/store/ui";
import { Button } from "@/ui/kit/button";
import { cn } from "@/ui/kit/cn";
import { Dialog } from "@/ui/kit/dialog";
import { Segmented } from "@/ui/kit/segmented";
import { Tip } from "@/ui/kit/tooltip";
import {
  backup,
  importFile,
  openPreset,
  openStarter,
  openTheme,
  pickFile,
  pinAsB,
  removePreset,
} from "../actions";

function Strip({ theme, className }: { theme: Theme; className?: string }) {
  return (
    <div
      className={cn("flex overflow-hidden rounded-md ring-1 ring-black/40", className)}
      style={{ background: theme.palette.bg1 }}
    >
      <div
        className="flex w-1/3 flex-col justify-center gap-0.5 px-2 font-mono text-[9px]"
        style={{ color: theme.palette.fg1 }}
      >
        <span>
          <span style={{ color: theme.palette.purple }}>fn</span>{" "}
          <span style={{ color: theme.palette.blue }}>theme</span>
          <span style={{ color: theme.palette.fg2 }}>()</span>
        </span>
        <span style={{ color: theme.palette.green }}>"palette"</span>
      </div>
      {PALETTE_KEYS.filter((k) => !k.startsWith("bright")).map((k) => (
        <span key={k} className="flex-1" style={{ background: theme.palette[k] }} />
      ))}
    </div>
  );
}

function ago(iso: string): string {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString();
}

function Snapshots({ presetId }: { presetId: string }) {
  const snaps = useLiveQuery(
    () => db.snapshots.where("presetId").equals(presetId).reverse().sortBy("createdAt"),
    [presetId],
  );
  if (!snaps?.length)
    return <p className="p-3 text-faint text-xs">No snapshots yet. Every save adds one.</p>;
  return (
    <div className="divide-y divide-line">
      {snaps.map((s) => (
        <div key={s.id} className="flex items-center gap-3 px-3 py-2">
          <Strip theme={s.theme} className="h-6 w-40 shrink-0" />
          <div className="min-w-0 flex-1 text-xs">
            <div className={s.auto ? "text-muted" : "font-medium text-fg"}>{s.label}</div>
            <div className="text-faint">{ago(s.createdAt)}</div>
          </div>
          <Tip label="Pin as B to compare with your draft">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => pinAsB(s.theme, `${s.theme.name} · ${s.label} ${ago(s.createdAt)}`)}
            >
              <Columns2 /> B
            </Button>
          </Tip>
          <Button
            size="sm"
            variant="outline"
            onClick={() => openTheme({ ...s.theme }, useEditor.getState().saved)}
          >
            Restore
          </Button>
        </div>
      ))}
    </div>
  );
}

export function PresetsDialog() {
  const open = useUi((s) => s.dialog === "presets");
  const close = () => useUi.getState().openDialog(null);
  const presets = useLiveQuery(() => db.presets.orderBy("updatedAt").reverse().toArray(), []);
  const current = useEditor((s) => s.draft.id);
  const [tab, setTab] = useState<"mine" | "starters">("mine");
  const [snapFor, setSnapFor] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const list = (presets ?? [])
    .filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => Number(b.favorite) - Number(a.favorite));

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && close()}
      title="Presets"
      description="Saved in this browser (IndexedDB). Back up to a file to move them between machines."
      className="h-[min(86vh,760px)] w-[min(94vw,900px)]"
      actions={
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              const f = await pickFile();
              if (f) {
                await importFile(f);
                close();
              }
            }}
          >
            <Upload /> Import
          </Button>
          <Button size="sm" variant="ghost" onClick={backup}>
            <Download /> Back up
          </Button>
        </div>
      }
    >
      <div className="flex items-center gap-2 border-line border-b px-5 py-2.5">
        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            { value: "mine", label: `Mine (${presets?.length ?? 0})` },
            { value: "starters", label: `Starters (${STARTERS.length})` },
          ]}
        />
        {tab === "mine" && (
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="h-7 flex-1 rounded-md border border-line bg-bg px-2 text-xs outline-none focus:border-accent"
          />
        )}
      </div>
      {tab === "starters" ? (
        <div className="grid grid-cols-2 gap-3 p-5">
          {STARTERS.map((s) => {
            const t = starterTheme(s.id) as Theme;
            return (
              <div
                key={s.id}
                className="group rounded-lg border border-line p-2.5 hover:border-accent/60"
              >
                <Strip theme={t} className="h-12" />
                <div className="mt-2 flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{s.name}</div>
                    <div className="truncate text-[11px] text-faint">{s.credit}</div>
                  </div>
                  <Tip label="Compare: pin as B">
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label="Pin as B"
                      onClick={() => pinAsB(t, s.name)}
                    >
                      <Columns2 />
                    </Button>
                  </Tip>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      openStarter(s.id);
                      close();
                    }}
                  >
                    Start from
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : list.length === 0 ? (
        <div className="p-10 text-center text-muted text-sm">
          <p>No saved presets yet.</p>
          <p className="mt-1 text-faint text-xs">
            Press Ctrl+S to save the current draft, or start from a starter.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-line">
          {list.map((p) => (
            <div key={p.id}>
              <div
                className={cn(
                  "flex items-center gap-3 px-5 py-2.5",
                  p.id === current && "bg-raised/50",
                )}
              >
                <Strip theme={p.theme} className="h-9 w-56 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 font-medium text-sm">
                    {p.name}
                    {p.id === current && (
                      <span className="rounded bg-accent/20 px-1.5 text-[10px] text-accent">
                        open
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-faint">
                    {p.theme.variant} · edited {ago(p.updatedAt)}
                    {p.theme.meta.basedOn && p.theme.meta.basedOn !== p.name
                      ? ` · from ${p.theme.meta.basedOn}`
                      : ""}
                  </div>
                </div>
                <Tip label={p.favorite ? "Unfavorite" : "Favorite"}>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label="Favorite"
                    onClick={() => db.presets.update(p.id, { favorite: !p.favorite })}
                  >
                    <Star className={p.favorite ? "fill-warn text-warn" : ""} />
                  </Button>
                </Tip>
                <Tip label="Snapshots">
                  <Button
                    size="icon-sm"
                    variant={snapFor === p.id ? "default" : "ghost"}
                    aria-label="Snapshots"
                    onClick={() => setSnapFor(snapFor === p.id ? null : p.id)}
                  >
                    <History />
                  </Button>
                </Tip>
                <Tip label="Pin as B">
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label="Pin as B"
                    onClick={() => pinAsB(p.theme, p.name)}
                  >
                    <Columns2 />
                  </Button>
                </Tip>
                <Tip label="Duplicate">
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label="Duplicate"
                    onClick={() => savePreset(cloneTheme(p.theme, `${p.name} copy`))}
                  >
                    <Copy />
                  </Button>
                </Tip>
                <Tip label="Delete">
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label="Delete"
                    onClick={() => removePreset(p.id)}
                  >
                    <Trash2 />
                  </Button>
                </Tip>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    openPreset(p);
                    close();
                  }}
                >
                  <FolderOpen /> Open
                </Button>
              </div>
              {snapFor === p.id && (
                <div className="mx-5 mb-3 rounded-lg border border-line bg-bg">
                  <Snapshots presetId={p.id} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Dialog>
  );
}
